import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAuth } from "@/context/AuthContext";
import { GuideContext } from "@/context/GuideContext";
import BrandButton from "@/components/ui/BrandButton";
import { DEFAULT_AVATAR_URL } from "@/const";
import { useNotifications } from "@/hooks/useNotifications";
import { formatNotificationTime } from "@/services/notificationService";
import LanguageSelector from "./LanguageSelector";
import TourAuthoringWrapper, { isAuthoringEnabled } from "@/components/tour/authoring";
import "./topnavbar.css";

const TopNavbar = ({ isPermanentlyExpanded, toggleSidebar, onBeforeAction }) => {
  const { t, i18n } = useTranslation();
  const notificationDropdownRef = useRef(null);
  const projectDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  const { user, switchProject, logout } = useAuth();

  const role = user?.role;
  const client_id = user?.client_id;
  const projects = user?.projects || [];
  const currentProject = projects.find((p) => p.id === client_id);
  const otherProjects = projects.filter((p) => p.id !== client_id);

  const { guideMode, toggleGuideMode, requestTour } = useContext(GuideContext);

  // Turning guide mode on replays the current page's tour (src/guides/tours.js)
  // and reveals inline info hotspots; turning it off hides the hotspots.
  const handleGuideClick = useCallback(() => {
    if (!guideMode) {
      requestTour();
    }
    toggleGuideMode();
  }, [guideMode, toggleGuideMode, requestTour]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAuthoringPanel, setShowAuthoringPanel] = useState(false);

  const isSuperAdminOrQa = role === "super-admin" || role === "qa";
  const canAuthorTours = isAuthoringEnabled() && isSuperAdminOrQa;

  const {
    notifications,
    unreadCount,
    isLoading: isLoadingNotifications,
    markAsSeen: markNotificationAsSeen,
    markAllAsRead,
    refreshNotifications,
    dismissAllToasts,
  } = useNotifications();

  const toggleNotifications = () => {
    const nextState = !showNotifications;
    setShowNotifications(nextState);
    setShowUserMenu(false);

    if (nextState) {
      dismissAllToasts();
      refreshNotifications({ silent: true });
    }
  };

  const changeProject = (project) => {
    if (
      typeof onBeforeAction === "function" &&
      !onBeforeAction({ type: "project-switch", project })
    ) {
      return;
    }
    switchProject(project.id);
  };

  const toggleProjectDropdown = () => {
    setShowProjectDropdown(!showProjectDropdown);
    setShowNotifications(false);
    setShowUserMenu(false);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    setShowNotifications(false);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_seen) {
      markNotificationAsSeen(notification.notification_id);
    }
  };

  const handleLogout = () => {
    if (
      typeof onBeforeAction === "function" &&
      !onBeforeAction({ type: "logout" })
    ) {
      return;
    }
    logout();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(event.target)
      ) {
        setShowProjectDropdown(false);
      }
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <nav className="top-navbar">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center w-100">
          {/* Hamburger Menu Toggle Button */}
          <div className="page-title d-flex align-items-center">
            <button
              className="btn sidebar-toggle-btn me-3"
              onClick={toggleSidebar}
              title={
                isPermanentlyExpanded
                  ? t("topbar.collapseSidebar", "Collapse Sidebar")
                  : t("topbar.expandSidebar", "Expand Sidebar")
              }
            >
              <i className="bi bi-list"></i>
            </button>
          </div>

            <div className="navbar-actions d-flex align-items-center">
              {/* Language Selector */}
              <LanguageSelector className="me-3" />

              {/* Guide Mode Toggle */}
              <BrandButton
                onClick={handleGuideClick}
                dataTestId="guide-button"
                className="me-3"
                style={guideMode ? {} : { opacity: 0.5 }}
                ariaPressed={guideMode}
                ariaLabel={
                  guideMode
                    ? t("topbar.guideModeOn", "Guide mode on")
                    : t("topbar.guideModeOff", "Guide mode off")
                }
              >
                {t("topbar.guide", "Guide")}{" "}
                <i
                  className="bi bi-question-circle ms-2"
                  aria-hidden="true"
                ></i>
              </BrandButton>

              {/* Staging/Dev Tour Authoring Toggle */}
              {canAuthorTours && (
                <button
                  type="button"
                  className={`tour-author-toggle me-3 ${
                    showAuthoringPanel ? "active" : ""
                  }`}
                  data-testid="tour-author-button"
                  onClick={() => setShowAuthoringPanel((prev) => !prev)}
                  aria-pressed={showAuthoringPanel}
                  aria-label={t("tour.author", "Author tour")}
                >
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                  <span>{t("tour.author", "Author tour")}</span>
                </button>
              )}
            {/* Change Project */}
            {otherProjects.length > 0 && (
              <div className="dropdown me-3" ref={projectDropdownRef}>
                <button
                  className="btn d-flex align-items-center user-profile-btn"
                  style={{
                    border: "1px solid #ced4da",
                    borderRadius: "0.375rem",
                  }}
                  onClick={toggleProjectDropdown}
                  aria-expanded={showProjectDropdown}
                >
                  <i className="bi bi-folder2-open me-2"></i>
                  <span className="user-name d-none d-md-block">
                    <b>{currentProject?.name || client_id}</b>
                    <i className="bi bi-chevron-down ms-1"></i>
                  </span>
                </button>
                {showProjectDropdown && (
                  <div className="dropdown-menu user-menu show">
                    {otherProjects.map((project, index) => (
                      <button
                        key={index}
                        className="dropdown-item"
                        onClick={() => changeProject(project)}
                      >
                        {project.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Notifications */}
            <div className="dropdown me-3" ref={notificationDropdownRef}>
              <button
                className="btn position-relative notification-btn"
                aria-label={t("topbar.notifications", "Notifications")}
                onClick={toggleNotifications}
              >
                <i className="bi bi-bell"></i>
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="dropdown-menu notification-menu show">
                  <div className="notification-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      {t("topbar.notifications", "Notifications")}
                    </h6>
                    {unreadCount > 0 && (
                      <button
                        className="btn btn-sm btn-link p-0"
                        onClick={markAllAsRead}
                      >
                        {t("topbar.markAllAsRead", "Mark all as read")}
                      </button>
                    )}
                  </div>

                  <div className="notification-list">
                    {isLoadingNotifications ? (
                      <div className="notification-item text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">
                            {t("topbar.loading", "Loading...")}
                          </span>
                        </div>
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="notification-item text-center text-muted">
                        <i
                          className="bi bi-bell-slash mb-2 d-block"
                          style={{ fontSize: "2rem" }}
                        ></i>
                        {t("topbar.noNotificationsYet", "No notifications yet")}
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.notification_id}
                          className={`notification-item ${
                            !notification.is_seen ? "unread" : ""
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="notification-icon">
                            <i className="bi bi-info-circle text-primary"></i>
                          </div>
                          <div className="notification-content">
                            <p
                              className={!notification.is_seen ? "fw-bold" : ""}
                            >
                              {notification.message}
                            </p>
                            <div className="d-flex align-items-center justify-content-between mt-1">
                              <small className="text-muted">
                                {formatNotificationTime(
                                  notification.created_at,
                                  t,
                                  i18n.resolvedLanguage || i18n.language
                                )}
                              </small>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 py-0 px-2"
                                style={{ fontSize: "11px", borderRadius: "12px" }}
                                title={t("topbar.discussWithAssistant", "Discuss with AI Assistant")}
                                aria-label={t("topbar.discussWithAssistant", "Discuss with AI Assistant")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!notification.is_seen) {
                                    markNotificationAsSeen(notification.notification_id);
                                  }
                                  if (typeof window.triggerElelemAssistantPrompt === "function") {
                                    window.triggerElelemAssistantPrompt(
                                      t("topbar.discussNotificationPrompt", {
                                        message: notification.message,
                                        defaultValue: `Please review this notification and analyze the results: "${notification.message}"`,
                                      })
                                    );
                                    setShowNotifications(false);
                                  }
                                }}
                              >
                                <i className="bi bi-stars"></i> {t("topbar.discussWithAssistant", "Discuss with AI Assistant")}
                              </button>
                            </div>
                          </div>
                          {!notification.is_seen && (
                            <div className="unread-indicator">
                              <span className="badge bg-primary rounded-pill">
                                •
                              </span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {notifications.length > 0 && (
                    <div className="notification-footer text-center p-2">
                      <small className="text-muted">
                        {t("topbar.showingLatestNotifications", {
                          count: notifications.length,
                          defaultValue: `Showing latest ${notifications.length} notifications`,
                        })}
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Profile */}
            <div className="dropdown" ref={userDropdownRef}>
              <button
                className="btn d-flex align-items-center user-profile-btn"
                onClick={toggleUserMenu}
              >
                <div className="avatar me-2">
                  <img
                    src={
                      user?.profile_picture ||
                      DEFAULT_AVATAR_URL
                    }
                    alt="User"
                    className="rounded-circle"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_AVATAR_URL;
                    }}
                  />
                </div>
                <span className="user-name d-none d-md-block">
                  {user?.firstname}{" "}
                  <b>{user?.lastname} </b>
                </span>
                <i className="bi bi-chevron-down ms-1"></i>
              </button>

              {showUserMenu && (
                <div className="dropdown-menu user-menu show">
                  <a className="dropdown-item" href="/profile">
                    <i className="bi bi-person me-2"></i>
                    {t("userMenu.profile", "Profile")}
                  </a>
                  <a className="dropdown-item" href="/settings">
                    <i className="bi bi-gear me-2"></i>
                    {t("userMenu.settings", "Settings")}
                  </a>
                  {(role === "admin" || role === "super-admin") && (
                    <a className="dropdown-item" href="/member">
                      <i className="bi bi-people me-2"></i>
                      {t("userMenu.members", "Members")}
                    </a>
                  )}
                  {role === "super-admin" && (
                    <a className="dropdown-item" href="/super-admin">
                      <i className="bi bi-shield-lock me-2"></i>
                      {t("userMenu.superAdminPanel", "Super Admin Panel")}
                    </a>
                  )}
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2"></i>
                    {t("userMenu.logout", "Logout")}
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </nav>
      {showAuthoringPanel && canAuthorTours && (
        <TourAuthoringWrapper onClose={() => setShowAuthoringPanel(false)} />
      )}
    </>
  );
};

export default TopNavbar;
